import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { insights, businesses } from '../mockData';

const InsightsPage = () => {
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(
    () => (filter === 'all' ? insights : insights.filter((i) => i.business === filter)),
    [filter]
  );

  const [featured, ...rest] = filtered;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Lockene Insights',
    url: 'https://lockene.info/insights',
    blogPost: insights.map((i) => ({
      '@type': 'BlogPosting',
      headline: i.title,
      description: i.excerpt,
      datePublished: i.date,
      author: { '@type': 'Person', name: i.author.name },
      articleSection: i.category
    }))
  };

  const filters = [
    { id: 'all', label: 'All' },
    ...businesses.map((b) => ({ id: b.id, label: b.name.replace('Lockene ', '').replace(' Real Estate', '').replace(' Sports Cafe', '') }))
  ];

  return (
    <>
      <Seo
        title="Insights · Lockene"
        description="Editorial writing from senior operators across Lockene AI CRM, Compliances, Styx Sports Cafe and Locacres."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="insights-heading" data-testid="insights-hero">
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Insights</span>
              </div>
              <h1 id="insights-heading" className="font-display text-5xl md:text-7xl leading-[1.03] tracking-tight text-brand-oxford">
                Written by senior operators. About the work.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-brand-slate leading-relaxed">
                Short-form editorial from the people who actually run our engagements — on AI-native selling,
                regulatory strategy, premium real estate and modern hospitality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-y border-brand-hairline py-6" aria-label="Filter by business">
        <div className="container-editorial flex items-center gap-2 md:gap-3 flex-wrap">
          <span className="eyebrow mr-2">Filter</span>
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`insight-filter-${f.id}`}
              className={`text-[12px] tracking-[0.14em] uppercase font-semibold px-4 py-2 border transition-colors focus-ring ${
                filter === f.id
                  ? 'bg-brand-oxford text-brand-alabaster border-brand-oxford'
                  : 'bg-transparent text-brand-slate border-brand-hairline hover:border-brand-oxford hover:text-brand-oxford'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* Featured + list */}
      <section className="section bg-brand-alabaster" aria-labelledby="feat-insight-heading">
        <div className="container-editorial">
          {featured ? (
            <Reveal className="mb-16 md:mb-24">
              <Link
                to={`/insights/${featured.slug}`}
                data-testid={`insight-featured-${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-white border border-brand-hairline hover:shadow-lift transition-shadow"
              >
                <div className="lg:col-span-6 relative overflow-hidden">
                  <img src={featured.heroImage} alt={featured.title} className="w-full h-72 lg:h-[440px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="eager" />
                </div>
                <div className="lg:col-span-6 p-8 md:p-12">
                  <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">
                    Featured · {featured.category} · <time dateTime={featured.date}>{new Date(featured.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</time>
                  </div>
                  <h2 id="feat-insight-heading" className="font-display text-3xl md:text-5xl leading-[1.1] text-brand-oxford mt-4">
                    {featured.title}
                  </h2>
                  <p className="mt-6 text-lg text-brand-slate leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-8 flex items-center justify-between gap-6 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold text-brand-oxford">{featured.author.name}</div>
                      <div className="text-xs text-brand-slate">{featured.author.role}</div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 group-hover:gap-3 transition-all">
                      Read the piece <ArrowUpRight size={14} strokeWidth={1.5} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ) : (
            <p className="text-brand-slate">No insights in this category yet.</p>
          )}

          {rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {rest.map((post, i) => (
                <Reveal key={post.slug} delay={i * 60}>
                  <Link
                    to={`/insights/${post.slug}`}
                    data-testid={`insight-card-${post.slug}`}
                    className="group bg-white border border-brand-hairline hover:shadow-lift transition-shadow flex flex-col h-full"
                  >
                    <div className="relative overflow-hidden">
                      <img src={post.heroImage} alt={post.title} className="w-full h-56 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">
                        {post.category} · {post.readMinutes} min read
                      </div>
                      <h3 className="font-display text-2xl leading-tight text-brand-oxford mt-3">{post.title}</h3>
                      <p className="mt-3 text-brand-slate leading-relaxed flex-1">{post.excerpt}</p>
                      <div className="mt-6 pt-6 border-t border-brand-hairline">
                        <div className="text-sm font-semibold text-brand-oxford">{post.author.name}</div>
                        <div className="text-xs text-brand-slate">{post.author.role}</div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default InsightsPage;
